// TRIAGE-02 (audit decision 3.2 — IMP-01/02/05) — the decision dispatch
// behind the triage keys:
//
//   accept            → undoable promote (single-finding endpoint, the
//                       same `useUndoablePromoteFindingMutation` the
//                       List row button fires)
//   dismiss           → INSTANT undoable dismiss (Q3: no confirmation,
//                       no reason; Ctrl+Z restores within the session)
//   dismissWithReason → small dialog with preset chips (chip click =
//                       submit immediately) + free text (Ctrl+Enter)
//   setSeverity(1..5) → PATCH the active/multi-selected DEFECTS' severity
//                       (grade → enum position; AI findings no-op —
//                       severity belongs to defects, IMP-05)
//
// Auto-advance (Q2, default ON, settings toggle): after accept/dismiss
// the layer fires-and-advances (IMP-03 — the optimistic cache flip makes
// the decided row non-pending immediately): next pending finding on the
// image, else the next image with pending findings (first pending
// auto-selects on landing via the activation effect).
//
// Feedback: ONE snackbar slot (bottom-right, no layout shift) with an
// Undo action wired to the global undo trigger (IMP-06 lite). The layer
// must not depend on FindingsPanel being mounted (pop-out / hidden
// List), so it renders its own snackbar + reason dialog.
import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Stack,
  TextField,
} from "@mui/material";
import type { DefectSeverity } from "@aeriosiq/types";
import {
  useUndoableDismissFindingMutation,
  useUndoablePromoteFindingMutation,
} from "../../findings/queries.js";
import {
  useUndoableUpdateDefectMutation,
  useDefectsQuery,
} from "../../defects/queries.js";
import { gradeToSeverity } from "../../defects/severityDisplay.js";
import { isGradingReady } from "../state/analysisReadinessStore.js";
import { useImageActivationStore } from "../state/imageActivationStore.js";
import { useSelectionStore } from "../state/selectionStore.js";
import { useUndoStackStore } from "../state/undoStackStore.js";
import { useClassDisplayNames } from "../useClassDisplayNames.js";
import { useTriagePreferencesStore } from "./triagePreferencesStore.js";
import { usePendingFindingCursor } from "./usePendingFindingCursor.js";
import { useTriageQueue } from "./useTriageQueue.js";
import { DISMISS_REASON_PRESETS } from "./dismissReasons.js";
import type {
  SeverityGrade,
  TriageDecisionHandlers,
} from "./useTriageHotkeys.js";

/**
 * Grade (1..5) → contract severity enum, IMP-05's named-enum mapping.
 * Severity story (8.0.0): this ABSOLUTE table is the fallback when no
 * class-model context is available; with a resolved model the mapping is
 * RELATIVE via `gradeToSeverity` (3-level → low/medium/high), mirroring
 * the gateway's read-side mapping so the round trip is stable.
 */
export const SEVERITY_BY_GRADE: Record<SeverityGrade, DefectSeverity> = {
  1: "info",
  2: "low",
  3: "medium",
  4: "high",
  5: "critical",
};

type TriageSnack =
  | { kind: "promoted"; label: string }
  | { kind: "dismissed" }
  | { kind: "severity"; label: string; count: number }
  | {
      kind: "error";
      action: "promote" | "dismiss" | "severity";
      errorId: string;
    };

export interface UseTriageDecisionsArgs {
  dataAcquisitionId: string;
  /** D-5 gate — handlers no-op when false (belt + braces; the hotkey
   *  layer already gates). */
  editAllowed: boolean;
  /**
   * Severity story (8.0.0 / UX Q6) — the DA's resolved effective class
   * model (`useEffectiveClassModel`). When present:
   *   * grade keys beyond `classCount` no-op (a 3-level model has no
   *     grade 4/5),
   *   * the PATCHed enum maps RELATIVE to the model,
   *   * the None model (classCount 0) disables the keys entirely,
   *   * feedback speaks grade vocabulary ("Severity grade 3 applied").
   * Absent (legacy/test mounts) → the absolute SEVERITY_BY_GRADE table.
   */
  classModel?: { classCount: number } | null;
}

export interface UseTriageDecisionsResult {
  handlers: TriageDecisionHandlers;
  /** Snackbar + Shift+X reason dialog — render once in the host. */
  element: React.ReactElement;
}

export function useTriageDecisions({
  dataAcquisitionId,
  editAllowed,
  classModel = null,
}: UseTriageDecisionsArgs): UseTriageDecisionsResult {
  const promote = useUndoablePromoteFindingMutation(dataAcquisitionId);
  const dismiss = useUndoableDismissFindingMutation(dataAcquisitionId);
  const updateDefect = useUndoableUpdateDefectMutation(dataAcquisitionId);
  // Same params as AnalysisLayoutMount's defects queries → cache hit.
  const defectsQuery = useDefectsQuery(dataAcquisitionId, { pageSize: 200 });
  const cursor = usePendingFindingCursor(dataAcquisitionId);
  const queue = useTriageQueue(dataAcquisitionId);
  const classNames = useClassDisplayNames(dataAcquisitionId);

  const [snack, setSnack] = useState<TriageSnack | null>(null);
  const [reasonDialog, setReasonDialog] = useState<{
    findingId: string;
  } | null>(null);
  const [reasonText, setReasonText] = useState("");

  /** Fire-and-advance (IMP-03): the optimistic flip already removed the
   *  decided row from `pending`, so advancing immediately is safe. */
  const advanceAfter = useCallback(
    (decidedId: string): void => {
      if (!useTriagePreferencesStore.getState().autoAdvance) return;
      if (cursor.selectNextPendingAfter(decidedId)) return;
      queue.goToNextPendingImage();
    },
    [cursor, queue],
  );

  const accept = useCallback(
    (findingId: string): void => {
      if (!editAllowed || !isGradingReady()) return;
      const finding = cursor.findings.find((f) => f.id === findingId);
      if (finding?.status !== "pending") return;
      const label = classNames.resolve(finding.aiClassName).display;
      promote.mutate(
        {
          findingId,
          imageId: finding.imageId,
          dataAcquisitionId,
        },
        {
          onSuccess: () => {
            setSnack({ kind: "promoted", label });
          },
          onError: (err) => {
            setSnack({
              kind: "error",
              action: "promote",
              errorId: err.errorId ?? "unknown",
            });
          },
        },
      );
      advanceAfter(findingId);
    },
    [
      advanceAfter,
      classNames,
      cursor.findings,
      dataAcquisitionId,
      editAllowed,
      promote,
    ],
  );

  const runDismiss = useCallback(
    (findingId: string, reason?: string): void => {
      if (!editAllowed || !isGradingReady()) return;
      const finding = cursor.findings.find((f) => f.id === findingId);
      if (finding?.status !== "pending") return;
      dismiss.mutate(
        {
          findingId,
          ...(reason ? { reason } : {}),
          imageId: finding.imageId,
          dataAcquisitionId,
        },
        {
          onSuccess: () => {
            setSnack({ kind: "dismissed" });
          },
          onError: (err) => {
            setSnack({
              kind: "error",
              action: "dismiss",
              errorId: err.errorId ?? "unknown",
            });
          },
        },
      );
      advanceAfter(findingId);
    },
    [advanceAfter, cursor.findings, dataAcquisitionId, dismiss, editAllowed],
  );

  const dismissWithReason = useCallback(
    (findingId: string): void => {
      if (!editAllowed || !isGradingReady()) return;
      setReasonText("");
      setReasonDialog({ findingId });
    },
    [editAllowed],
  );

  const setSeverity = useCallback(
    (grade: SeverityGrade): void => {
      if (!editAllowed || !isGradingReady()) return;
      // Severity story (8.0.0 / UX Q6) — grade keys speak the class
      // model's vocabulary when one is resolved:
      //   * None model (classCount 0) → severity is meaningless; no-op.
      //   * grade > classCount → invalid for this model; no-op.
      //   * mapping is RELATIVE (mirror of the gateway's read mapping).
      let severity: DefectSeverity;
      let displayLabel: string;
      if (classModel) {
        if (classModel.classCount === 0 || grade > classModel.classCount) {
          return;
        }
        const mapped = gradeToSeverity(grade, classModel.classCount);
        if (!mapped) return;
        severity = mapped;
        displayLabel = `grade ${String(grade)}`;
      } else {
        severity = SEVERITY_BY_GRADE[grade];
        displayLabel = severity;
      }
      const { activeFindingId, activeKind, selectedFindingIds } =
        useSelectionStore.getState();
      // IMP-05 — severity belongs to defects; with an AI finding active
      // the keys are a no-op. Targets = the multi-selection (when it
      // holds defect ids) plus the active defect.
      const candidateIds = new Set<string>(selectedFindingIds);
      if (activeFindingId !== null && activeKind === "defect") {
        candidateIds.add(activeFindingId);
      }
      if (candidateIds.size === 0) return;
      const defects = defectsQuery.data?.items ?? [];
      const targets = defects.filter(
        (d) => candidateIds.has(d.id) && d.severity !== severity,
      );
      if (targets.length === 0) return;
      const imageId = useImageActivationStore.getState().activeImageId ?? "";
      for (const d of targets) {
        updateDefect.mutate(
          {
            defectId: d.id,
            body: { severity },
            before: { severity: d.severity },
            after: { severity },
            dataAcquisitionId,
            imageId,
          },
          {
            onError: (err) => {
              setSnack({
                kind: "error",
                action: "severity",
                errorId: err.errorId ?? "unknown",
              });
            },
          },
        );
      }
      setSnack({
        kind: "severity",
        label: displayLabel,
        count: targets.length,
      });
    },
    [
      classModel,
      dataAcquisitionId,
      defectsQuery.data,
      editAllowed,
      updateDefect,
    ],
  );

  const handlers = useMemo<TriageDecisionHandlers>(
    () => ({
      accept,
      dismiss: runDismiss,
      dismissWithReason,
      setSeverity,
    }),
    [accept, runDismiss, dismissWithReason, setSeverity],
  );

  const closeReasonDialog = (): void => {
    setReasonDialog(null);
  };
  const submitReason = (reason: string): void => {
    const target = reasonDialog;
    closeReasonDialog();
    if (!target) return;
    runDismiss(target.findingId, reason.trim() || undefined);
  };

  const undoAction = (
    <Button
      color="inherit"
      size="small"
      onClick={() => {
        useUndoStackStore.getState().triggerUndo?.();
        setSnack(null);
      }}
    >
      Undo
    </Button>
  );

  const element = (
    <>
      {snack ? (
        <Snackbar
          open
          autoHideDuration={snack.kind === "error" ? 6000 : 4000}
          onClose={() => {
            setSnack(null);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          data-testid="triage-snackbar"
        >
          {snack.kind === "promoted" ? (
            <Alert severity="success" variant="filled" action={undoAction}>
              Promoted {snack.label} — Ctrl+Z to undo
            </Alert>
          ) : snack.kind === "dismissed" ? (
            <Alert severity="success" variant="filled" action={undoAction}>
              Finding dismissed — Ctrl+Z to undo
            </Alert>
          ) : snack.kind === "severity" ? (
            <Alert severity="success" variant="filled" action={undoAction}>
              {snack.count === 1
                ? `Severity ${snack.label} applied`
                : `Severity ${snack.label} applied to ${String(snack.count)} defects`}
            </Alert>
          ) : (
            <Alert
              severity="error"
              variant="filled"
              onClose={() => {
                setSnack(null);
              }}
            >
              {snack.action === "promote"
                ? "Promote"
                : snack.action === "dismiss"
                  ? "Dismiss"
                  : "Severity update"}{" "}
              failed (errorId: {snack.errorId})
            </Alert>
          )}
        </Snackbar>
      ) : null}

      {/* Shift+X — dismiss with reason. Chip click submits immediately;
          free text submits via Ctrl+Enter or the Dismiss button. */}
      <Dialog
        open={reasonDialog !== null}
        onClose={closeReasonDialog}
        maxWidth="xs"
        fullWidth
        aria-labelledby="triage-dismiss-reason-title"
        data-testid="triage-dismiss-reason-dialog"
      >
        <DialogTitle id="triage-dismiss-reason-title">
          Dismiss with reason
        </DialogTitle>
        <DialogContent>
          <Stack
            direction="row"
            spacing={0.5}
            useFlexGap
            flexWrap="wrap"
            sx={{ mb: 1.5, mt: 0.5 }}
          >
            {DISMISS_REASON_PRESETS.map((preset) => (
              <Chip
                key={preset}
                label={preset}
                size="small"
                onClick={() => {
                  submitReason(preset);
                }}
                data-testid={`triage-dismiss-reason-${preset.replace(/\s+/g, "-").toLowerCase()}`}
              />
            ))}
          </Stack>
          <TextField
            value={reasonText}
            onChange={(e) => {
              setReasonText(e.target.value);
            }}
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                e.preventDefault();
                submitReason(reasonText);
              }
            }}
            size="small"
            fullWidth
            placeholder="Custom reason… (Ctrl+Enter to dismiss)"
            multiline
            minRows={2}
            maxRows={4}
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={closeReasonDialog}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              submitReason(reasonText);
            }}
            data-testid="triage-dismiss-reason-submit"
          >
            Dismiss
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return { handlers, element };
}
